import {
  isValid,
  addMilliseconds,
  addSeconds,
  addMinutes,
  addHours,
  addDays,
  addWeeks,
  addQuarters,
  addMonths,
  addYears,
  isAfter,
  formatDuration,
  format as dateFNSFormat,
  subDays,
  subHours,
  subMilliseconds,
  subMinutes,
  subMonths,
  subQuarters,
  subSeconds,
  subWeeks,
  subYears,
  formatDistance,
} from "date-fns";
import { DEFAULT_LOCALE, EMPTY_STRING_LABEL } from "../core/constants/appGlobals";
import { DateWindow } from "../core/models/DateWindow";
import { ptBR } from 'date-fns/locale'
import { NullableDate } from "../core/models/ModelHelpers";

export type addTimeValidUnits =
  | "milliseconds"
  | "millisecond"
  | "ms"
  | "seconds"
  | "second"
  | "s"
  | "minutes"
  | "minute"
  | "m"
  | "hours"
  | "hour"
  | "h"
  | "days"
  | "day"
  | "d"
  | "weeks"
  | "week"
  | "w"
  | "months"
  | "month"
  | "mo"
  | "quarters"
  | "quarter"
  | "q"
  | "years"
  | "year"
  | "y";

export default function addTime(date: Date, delta: number, unit: addTimeValidUnits) {
  switch (unit) {
    case "milliseconds":
    case "millisecond":
    case "ms":
      return addMilliseconds(date, delta);

    case "seconds":
    case "second":
    case "s":
      return addSeconds(date, delta);

    case "minutes":
    case "minute":
    case "m":
      return addMinutes(date, delta);

    case "hours":
    case "hour":
    case "h":
      return addHours(date, delta);

    case "days":
    case "day":
    case "d":
      return addDays(date, delta);

    case "weeks":
    case "week":
    case "w":
      return addWeeks(date, delta);

    case "months":
    case "month":
    case "mo":
      return addMonths(date, delta);

    case "quarters":
    case "quarter":
    case "q":
      return addQuarters(date, delta);

    case "years":
    case "year":
    case "y":
      return addYears(date, delta);
  }
}

export function subTime(date: Date, delta: number, unit: addTimeValidUnits) {
  switch (unit) {
    case "milliseconds":
    case "millisecond":
    case "ms":
      return subMilliseconds(date, delta);

    case "seconds":
    case "second":
    case "s":
      return subSeconds(date, delta);

    case "minutes":
    case "minute":
    case "m":
      return subMinutes(date, delta);

    case "hours":
    case "hour":
    case "h":
      return subHours(date, delta);

    case "days":
    case "day":
    case "d":
      return subDays(date, delta);

    case "weeks":
    case "week":
    case "w":
      return subWeeks(date, delta);

    case "months":
    case "month":
    case "mo":
      return subMonths(date, delta);

    case "quarters":
    case "quarter":
    case "q":
      return subQuarters(date, delta);

    case "years":
    case "year":
    case "y":
      return subYears(date, delta);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDateValid(date: any) {
  if (date && isValid(new Date(date)) && isAfter(new Date(date), new Date(1, 1, 1))) {
    return true;
  }

  return false;
}

export type dateWindowBuildOptions = "2d" | "1w" | "2w" | "1M" | "2M" | "3M" | "6M";

export function buildDateWindow(option: dateWindowBuildOptions): DateWindow {
  let start = new Date(new Date().setHours(0, 0, 0, 0));

  const end = new Date(new Date().setHours(23, 59, 59, 59));

  switch (option) {
    case "2d":
      start = addTime(start, -2, "d");
      break;
    case "1w":
      start = addTime(start, -7, "d");
      break;
    case "2w":
      start = addTime(start, -14, "d");
      break;
    case "1M":
      start = addTime(start, -1, "mo");
      break;
    case "2M":
      start = addTime(start, -2, "mo");
      break;
    case "3M":
      start = addTime(start, -3, "mo");
      break;
    case "6M":
      start = addTime(start, -6, "mo");
      break;
    default:
      throw new Error("invalid date window option!");
  }

  return { start, end };
}

interface formatDurationOptions {
  format?: ["years", "months", "weeks", "days", "hours", "minutes", "seconds"];
  zero?: boolean;
  delimiter?: string;
  locale?: Locale;
}

export function localeFormatDuration(duration: Duration, options?: formatDurationOptions): string {
  return formatDuration(duration, {
    format: ["years", "months", "weeks", "days", "hours", "minutes"],
    locale: DEFAULT_LOCALE,
    ...options,
  });
}

export const formatDateTime = (date?: Date | string, format?: string): string => {
  if (!isDateValid(date)) {
    return EMPTY_STRING_LABEL;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return dateFNSFormat(new Date(date!), format || "dd/MM/yyyy HH:mm", { locale: ptBR });
};

export function format(date: number | Date, formatStr: string) {
  return dateFNSFormat(date, formatStr, { locale: DEFAULT_LOCALE });
}

export function convertDateFromTimezonePtBr(
  date_parameter: Date | string | undefined,
  onlyDate?: boolean
): string {
  let date = date_parameter;

  if (date) {
    if (typeof date === "string") {
      if (date.includes("+00:00")) {
        date = date.replace("+00:00", "");
      }
      date = new Date(date);
    }

    return keepDateFromTimezonePtBr(date, onlyDate);
  }
  return EMPTY_STRING_LABEL;
}

export function dateFromTimezonePtBr(
  date_parameter: Date | string | undefined,
  onlyDate?: boolean
): Date {
  let date = date_parameter;

  if (date) {
    if (typeof date === "string") date = new Date(date);

    return date;
  }
  return new Date();
}

export function keepDateFromTimezonePtBr(
  date_parameter: Date | string | undefined,
  onlyDate?: boolean
): string {
  let date = date_parameter;

  if (date) {
    const date_format = {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Sao_Paulo",
    };

    if (typeof date === "string") date = new Date(date);

    if (onlyDate) {
      delete date_format.hour;
      delete date_format.minute;
      date_format.year = "numeric";
      return date.toLocaleString("en-CA", date_format as Intl.DateTimeFormatOptions) + "T00:00:00";
    }

    return date.toLocaleString("pt-BR", date_format as Intl.DateTimeFormatOptions);
  }
  return EMPTY_STRING_LABEL;
}

export function getDateFromTimezonePtBrToGMT(
  date_parameter: Date | string | undefined,
  onlyDate?: boolean
): string {
  let date = date_parameter;

  if (date) {
    const date_format = {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "GMT",
    };

    if (typeof date === "string") date = new Date(date);

    if (onlyDate) {
      delete date_format.hour;
      delete date_format.minute;
      date_format.year = "numeric";
      return date.toLocaleString("en-CA", date_format as Intl.DateTimeFormatOptions) + "T00:00:00";
    }

    return date.toLocaleString("pt-BR", date_format as Intl.DateTimeFormatOptions);
  }
  return EMPTY_STRING_LABEL;
}

// Trip/delivery-window dates from the API are displayed using their raw GMT components
// (same convention as getDateFromTimezonePtBrToGMT, used for trip dates elsewhere), not
// shifted to America/Sao_Paulo - that would double-convert and show the wrong wall-clock time.
export function formatDateTimeBr(date?: Date | string, onlyTime?: boolean): string {
  if (!isDateValid(date)) {
    return EMPTY_STRING_LABEL;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const useDate = typeof date === "string" ? new Date(date!) : (date as Date);

  if (onlyTime) {
    return useDate.toLocaleTimeString("pt-BR", {
      timeZone: "GMT",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return useDate.toLocaleDateString("pt-BR", { timeZone: "GMT" });
}

export function getFixedTimezoneDate(date: Date) {
  const dateTime = date.getTime();
  const dateOffset: number = date.getTimezoneOffset() * 60000;
  const dateWithTimezone: number = dateTime - dateOffset;
  return new Date(dateWithTimezone);
}

export function calculateDays(date?: Date | NullableDate, date2?: Date | NullableDate) {
  if (date && date2) {
    return formatDistance(date2, date, { locale: ptBR });
  }
  return null;
}